import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, BookOpen, Users, Lock, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotesPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [myNotes, setMyNotes] = useState([]);
  const [partyNotes, setPartyNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [partyDialogOpen, setPartyDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingParty, setEditingParty] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "", session_number: null });
  const [partyFormData, setPartyFormData] = useState({ title: "", content: "", session_number: null });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, myNotesRes, partyNotesRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/my-notes`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/party-notes`, { withCredentials: true })
      ]);
      setCampaign(campaignRes.data);
      setMyNotes(myNotesRes.data);
      setPartyNotes(partyNotesRes.data);
    } catch (error) {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMyNote = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, campaign_id: campaignId };
      if (editing) {
        await axios.put(`${API}/player-notes/${editing.id}`, payload, { withCredentials: true });
        toast.success("Note updated");
      } else {
        await axios.post(`${API}/player-notes`, payload, { withCredentials: true });
        toast.success("Note created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save note");
    }
  };

  const handleSubmitPartyNote = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...partyFormData, campaign_id: campaignId };
      if (editingParty) {
        await axios.put(`${API}/party-notes/${editingParty.id}`, payload, { withCredentials: true });
        toast.success("Party note updated");
      } else {
        await axios.post(`${API}/party-notes`, payload, { withCredentials: true });
        toast.success("Party note created");
      }
      setPartyDialogOpen(false);
      resetPartyForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save party note");
    }
  };

  const handleDeleteMyNote = async (id) => {
    const ok = await confirm("Delete Note", "Are you sure you want to delete this note?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/player-notes/${id}`, { withCredentials: true });
      toast.success("Note deleted");
      if (selectedNote?.id === id) setSelectedNote(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const handleDeletePartyNote = async (id) => {
    const ok = await confirm("Delete Party Note", "Are you sure you want to delete this shared note?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/party-notes/${id}`, { withCredentials: true });
      toast.success("Party note deleted");
      if (selectedNote?.id === id) setSelectedNote(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete party note");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ title: "", content: "", session_number: null });
  };

  const resetPartyForm = () => {
    setEditingParty(null);
    setPartyFormData({ title: "", content: "", session_number: null });
  };

  const openEditMyNote = (note) => {
    setEditing(note);
    setFormData({ title: note.title, content: note.content || "", session_number: note.session_number });
    setDialogOpen(true);
  };

  const openEditPartyNote = (note) => {
    setEditingParty(note);
    setPartyFormData({ title: note.title, content: note.content || "", session_number: note.session_number });
    setPartyDialogOpen(true);
  };

  const handleDocUpload = async (e, noteType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    setUploadingDoc(true);
    try {
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/upload-document?note_type=${noteType}`,
        formDataUpload,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`"${res.data.title}" imported as ${noteType === 'party' ? 'party' : 'private'} note`);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to import document";
      toast.error(msg);
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="notes-page">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-serif text-foreground">Session Notes</h1>
          <p className="text-muted-foreground text-sm">Keep track of your adventures</p>
        </div>

        <Tabs defaultValue="my-notes" className="w-full">
          <TabsList className="bg-muted mb-6">
            <TabsTrigger value="my-notes" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              My Notes
            </TabsTrigger>
            <TabsTrigger value="party-notes" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Party Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-notes">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">Private notes only you can see</p>
              <div className="flex gap-2">
                <input
                  ref={docInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  className="hidden"
                  onChange={(e) => handleDocUpload(e, 'private')}
                  data-testid="doc-upload-input-private"
                />
                <Button
                  variant="outline"
                  onClick={() => docInputRef.current?.click()}
                  disabled={uploadingDoc}
                  className="border-border rounded-sm"
                  data-testid="upload-doc-private-button"
                >
                  {uploadingDoc ? (
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import Doc
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={resetForm}
                    data-testid="create-my-note-button"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Note
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                      {editing ? "Edit Note" : "New Private Note"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitMyNote} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="text-sm font-medium text-foreground block mb-2">Title</label>
                        <Input
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="Session 1 Notes"
                          required
                          data-testid="note-title-input"
                          className="bg-input border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Session #</label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.session_number || ""}
                          onChange={(e) => setFormData({ ...formData, session_number: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder="1"
                          data-testid="note-session-input"
                          className="bg-input border-border rounded-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Content</label>
                      <RichTextEditor
                        value={formData.content}
                        onChange={(value) => setFormData({ ...formData, content: value })}
                        placeholder="Write your notes here..."
                      />
                    </div>
                    <Button 
                      type="submit"
                      data-testid="save-note-button"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                    >
                      {editing ? "Save Changes" : "Create Note"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {myNotes.length === 0 ? (
              <div className="empty-state">
                <BookOpen className="empty-state-icon text-muted-foreground" />
                <h3 className="text-xl font-serif text-foreground mb-2">No notes yet</h3>
                <p className="text-muted-foreground mb-6">Start writing your session notes</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  {myNotes.map((note) => (
                    <Card
                      key={note.id}
                      className={`bg-card border rounded-sm cursor-pointer entity-card ${
                        selectedNote?.id === note.id && selectedNote?.type === 'my' ? 'border-primary' : 'border-border'
                      }`}
                      onClick={() => setSelectedNote({ ...note, type: 'my' })}
                      data-testid={`my-note-${note.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-foreground">{note.title}</h4>
                            {note.session_number && (
                              <p className="text-xs text-muted-foreground">Session {note.session_number}</p>
                            )}
                          </div>
                          <Lock className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="lg:col-span-2">
                  {selectedNote?.type === 'my' ? (
                    <Card className="bg-card border-border rounded-sm">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h2 className="text-xl font-serif text-foreground">{selectedNote.title}</h2>
                            {selectedNote.session_number && (
                              <p className="text-sm text-muted-foreground">Session {selectedNote.session_number}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditMyNote(selectedNote)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteMyNote(selectedNote.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <RichTextDisplay content={selectedNote.content} />
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      Select a note to view
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="party-notes">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">Shared notes visible to all party members</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,.docx,.doc,.txt';
                    input.onchange = (e) => handleDocUpload(e, 'party');
                    input.click();
                  }}
                  disabled={uploadingDoc}
                  className="border-border rounded-sm"
                  data-testid="upload-doc-party-button"
                >
                  {uploadingDoc ? (
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import Doc
                </Button>
                <Dialog open={partyDialogOpen} onOpenChange={setPartyDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={resetPartyForm}
                    data-testid="create-party-note-button"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Party Note
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                      {editingParty ? "Edit Party Note" : "New Party Note"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitPartyNote} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="text-sm font-medium text-foreground block mb-2">Title</label>
                        <Input
                          value={partyFormData.title}
                          onChange={(e) => setPartyFormData({ ...partyFormData, title: e.target.value })}
                          placeholder="Important Quest Info"
                          required
                          data-testid="party-note-title-input"
                          className="bg-input border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Session #</label>
                        <Input
                          type="number"
                          min="1"
                          value={partyFormData.session_number || ""}
                          onChange={(e) => setPartyFormData({ ...partyFormData, session_number: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder="1"
                          data-testid="party-note-session-input"
                          className="bg-input border-border rounded-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Content</label>
                      <RichTextEditor
                        value={partyFormData.content}
                        onChange={(value) => setPartyFormData({ ...partyFormData, content: value })}
                        placeholder="Share your findings with the party..."
                      />
                    </div>
                    <Button 
                      type="submit"
                      data-testid="save-party-note-button"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                    >
                      {editingParty ? "Save Changes" : "Create Party Note"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {partyNotes.length === 0 ? (
              <div className="empty-state">
                <Users className="empty-state-icon text-muted-foreground" />
                <h3 className="text-xl font-serif text-foreground mb-2">No party notes yet</h3>
                <p className="text-muted-foreground mb-6">Share notes with your party</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  {partyNotes.map((note) => (
                    <Card
                      key={note.id}
                      className={`bg-card border rounded-sm cursor-pointer entity-card ${
                        selectedNote?.id === note.id && selectedNote?.type === 'party' ? 'border-primary' : 'border-border'
                      }`}
                      onClick={() => setSelectedNote({ ...note, type: 'party' })}
                      data-testid={`party-note-${note.id}`}
                    >
                      <CardContent className="p-4">
                        <h4 className="font-medium text-foreground">{note.title}</h4>
                        <p className="text-xs text-muted-foreground">
                          by {note.author_name} {note.session_number && `• Session ${note.session_number}`}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="lg:col-span-2">
                  {selectedNote?.type === 'party' ? (
                    <Card className="bg-card border-border rounded-sm">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h2 className="text-xl font-serif text-foreground">{selectedNote.title}</h2>
                            <p className="text-sm text-muted-foreground">
                              by {selectedNote.author_name} {selectedNote.session_number && `• Session ${selectedNote.session_number}`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditPartyNote(selectedNote)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeletePartyNote(selectedNote.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <RichTextDisplay content={selectedNote.content} />
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      Select a note to view
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default NotesPage;
